import { Col, Modal, Select, Spin, Tabs, Table as TableAntd } from 'antd';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalFooter } from '~/components/Modal';
import Row from '~/components/Row';
import { Permissions } from '~/services/permissions';
import { StyledTabs } from './style';
import { getFinancialExpenses } from '~/services/hooks/financialExpenses';
import { getFinancialExpenseItems } from '~/services/hooks/financialExpenseItems';
import { Table } from '~/components/Table';
import Button from '~/components/Button';
import FinancialExpenseItemExpensesModal from './financialExpenseItemExpensesModal';
import { useCheckAuth } from '~/components/Can/checkAuth';
import { formatPrice } from '~/Utils/format';
import { registerTableColumns, reportTableColumns } from '~/tables/financialExpenses';
import errorHandler from '~/Utils/errorHandler';

export default function RegisterModal({ visible, onClose, isMobile }) {
  const { t } = useTranslation();
  const authCheck = useCheckAuth();
  const [loading, setLoading] = useState(false);
  const [financialExItems, setFinancialExItems] = useState([]);
  const [financialEx, setFinancialEx] = useState([]);
  const [expenseType, setExpenseType] = useState(0);
  const [activeKey, setActiveKey] = useState(0);
  const [editFinancialExItem, setEditFinancialExItem] = useState(null);
  const [financialExItemExModalVisible, setFinancialExItemExModalVisible] = useState(false);
  const [report, setReport] = useState([]);
  const permissions = Permissions();
  const years = Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - i);
  const [selectedYear, setSelectedYear] = useState(years[0]);

  const fetchFinancialExpenseItems = async () => {
    setLoading(true);
    try {
      const paramsNoPag = {
        params: {
          selectedYear,
          financial_expense_id: activeKey,
          withMonth: true,
        },
      };

      const data = await getFinancialExpenseItems(paramsNoPag, 'no-paginated');

      setFinancialExItems(data);
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  const fetchFinancialEx = async () => {
    setLoading(true);
    try {
      const params = {
        params: {
          expense_type: expenseType,
        },
      };
      const data = await getFinancialExpenses(params, 'no-paginated');

      setFinancialEx(data);
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {
        params: {
          year: selectedYear,
        },
      };
      const data = await getFinancialExpenses(params, 'report');

      setReport(data);
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  const handleCreate = () => {
    setFinancialExItemExModalVisible(true);
  };

  const handleEdit = (financialExpenseItemId) => {
    setEditFinancialExItem(financialExpenseItemId);
    setFinancialExItemExModalVisible(true);
  };

  const renderContent = () => (
    <StyledTabs
      tabPosition="bottom"
      onChange={(key) => setActiveKey(key)}
      items={financialEx.map((expense) => ({
        label: expense.name || 'Despesa não especificada',
        key: expense.id || 'undefined-expense',
        children: (
          <>
            <Table
              pagination={false}
              rowKey="id"
              loading={loading}
              dataSource={financialExItems}
              columns={registerTableColumns(handleEdit)}
              bordered
            />
          </>
        ),
      }))}
    />
  );

  useEffect(() => {
    if (visible) {
      fetchFinancialExpenseItems();
    }
  }, [visible, activeKey, selectedYear]);

  useEffect(() => {
    if (visible) {
      fetchFinancialEx();
    }
  }, [visible, expenseType]);

  useEffect(() => {
    if (visible) {
      fetchReport();
    }
  }, [visible, selectedYear]);

  useEffect(() => {
    if (visible && financialEx.length > 0 && !activeKey) {
      setActiveKey(financialEx[0].id);
    }
  }, [visible, financialEx]);

  useEffect(() => {
    if (financialEx.length > 0) {
      setActiveKey(financialEx[0].id);
    }
  }, [expenseType, financialEx]);

  return (
    <>
      <Formik validateOnBlur={false} validateOnChange={false} enableReinitialize>
        {({ isSubmitting, submitForm, resetForm }) => (
          <Modal
            title={t('screens:expenses.register.title')}
            onCancel={onClose}
            style={{ maxWidth: isMobile ? '100%' : '1500px' }}
            afterClose={resetForm}
            open={visible}
            loading={isSubmitting}
            centered
            width="1500px"
            footer={
              <ModalFooter
                type={permissions && !permissions.includes('@expenses/edit') && 'info'}
                onOk={submitForm}
                loading={isSubmitting}
                onCancel={onClose}
              />
            }
          >
            <Row>
              <Col
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  marginBottom: 20,
                }}
              >
                <Select defaultValue={selectedYear} style={{ width: 200 }} onChange={(value) => setSelectedYear(value)}>
                  <Select.Option value="" disabled>
                    Selecionar Ano
                  </Select.Option>
                  {years.map((year) => (
                    <Select.Option key={year} value={year}>
                      {year}
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  disabled={expenseType === '3' || !authCheck('@registerExpenses/create')}
                  color="primary"
                  onClick={() => handleCreate(null)}
                >
                  Adicionar Registro
                </Button>
              </Col>
              <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                <Tabs defaultActiveKey={0} onChange={(key) => setExpenseType(key)}>
                  <Tabs.TabPane tab="Fixa" key={0}>
                    <Spin spinning={loading}>{renderContent()}</Spin>
                  </Tabs.TabPane>
                  <Tabs.TabPane tab="Fixa / Variável" key={1}>
                    <Spin spinning={loading}>{renderContent()}</Spin>
                  </Tabs.TabPane>
                  <Tabs.TabPane tab="Variável" key={2}>
                    <Spin spinning={loading}>{renderContent()}</Spin>
                  </Tabs.TabPane>
                  <Tabs.TabPane tab="Geral" key={3}>
                    <Spin spinning={loading}>
                      <Table
                        pagination={false}
                        rowKey="id"
                        loading={loading}
                        dataSource={report}
                        columns={reportTableColumns()}
                        bordered
                        summary={() => {
                          const months = [
                            'jan',
                            'feb',
                            'mar',
                            'apr',
                            'may',
                            'jun',
                            'jul',
                            'aug',
                            'sep',
                            'oct',
                            'nov',
                            'dec',
                            'total',
                          ];

                          const capitalizeFirstLetter = (string) => {
                            return string ? string[0].toUpperCase() + string.slice(1) : string;
                          };

                          const initialTotals = months.reduce((acc, month) => {
                            acc[month] = 0;
                            return acc;
                          }, {});

                          const total = report.reduce((acc, r) => {
                            months.forEach((month) => {
                              const value =
                                month === 'total'
                                  ? r?.montly_expenses?.total
                                  : r?.montly_expenses?.[capitalizeFirstLetter(month)]?.total;

                              const numericValue =
                                typeof value === 'string' ? parseFloat(value.replace(',', '.')) : Number(value) || 0;
                              acc[month] += numericValue;
                            });
                            return acc;
                          }, initialTotals);

                          return (
                            <TableAntd.Summary.Row style={{ backgroundColor: '#d6d6d6' }}>
                              <TableAntd.Summary.Cell>{t('TOTAL')}</TableAntd.Summary.Cell>
                              {months.map((key) => (
                                <TableAntd.Summary.Cell key={key}>{formatPrice(total[key])}</TableAntd.Summary.Cell>
                              ))}
                            </TableAntd.Summary.Row>
                          );
                        }}
                      />
                    </Spin>
                  </Tabs.TabPane>
                </Tabs>
              </Col>
            </Row>
          </Modal>
        )}
      </Formik>
      <FinancialExpenseItemExpensesModal
        visible={financialExItemExModalVisible}
        financialExId={activeKey}
        financialExItemId={editFinancialExItem}
        year={selectedYear}
        isMobile={isMobile}
        onClose={() => {
          setEditFinancialExItem(null);
          setFinancialExItemExModalVisible(false);
          fetchFinancialExpenseItems();
        }}
      />
    </>
  );
}
